import { RootInput } from "@/components/BaseComponents/Inputs";
import ImpactPill from "@/components/Impact/ImpactPill";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTag from "@/components/StatusTag/StatusTag";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import useTranslation from "@/hooks/useTranslation";
import DeleteTravelModeAction from "@/pages/employee/travel_modes/DeleteTravelModeAction";
import TravelRelations from "@/pages/employee/travel_modes/detail/TravelRelations";
import TravelModeCascader from "@/pages/employee/travel_modes/TravelModeCascader";
import { getOption } from "@/pages/employee/travel_modes/TravelReferenceSelector";
import {
  TravelMode,
  TravelModeOption,
  useDeleteTravelModeReferences,
  useGetTravelMode,
  useGetTravelReferenceOptions,
  usePatchTravelMode,
  usePostTravelModeReference,
} from "@/pages/employee/travel_modes/useTravelMode";
import { CheckOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Col, Form, message, Row, Space } from "antd";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./TravelModeDetail.module.scss";

function TravelModeDetail() {
  let { travelModeId: id } = useParams();
  const travelModeId = Number(id);

  const deleteTravalModeReference = useDeleteTravelModeReferences();
  const translation = useTranslation();
  const {
    data,
    isFetching: dataIsFetching,
    isLoading: dataIsLoading,
  } = useGetTravelMode({ travelModeId });

  const navigate = useNavigate();
  const { data: travelOptions, isLoading: OptionsIsLoading } = useGetTravelReferenceOptions();
  const usePost = usePostTravelModeReference();
  const usePatch = usePatchTravelMode();

  const smartQueryClient = useSmartQueryClient();

  const [selectedOption, setSelectedOption] = useState<TravelModeOption | undefined>(undefined);

  const [form] = Form.useForm();

  form.setFieldsValue({
    name: data?.name,
    id: data?.id,
  });

  useEffect(() => {
    if (data?.urn) {
      setSelectedOption(getOption(data.urn, travelOptions));
    } else {
      setSelectedOption(undefined);
    }
  }, [data]);

  const onDelete = () => {
    navigate("/employees/travel_modes");
  };

  const onChange = (_: any, selectedOptions: TravelModeOption[] | undefined) => {
    if (selectedOptions) {
      setSelectedOption(selectedOptions[selectedOptions.length - 1]);
      usePost
        .mutateAsync({
          travelIds: [travelModeId],
          urn: selectedOptions[selectedOptions.length - 1].value,
        })
        .then(() => {
          message.success("Impact reference successfully saved.");
        });
    } else {
      setSelectedOption(undefined);
      deleteTravalModeReference
        .mutateAsync({
          travelModeIds: [travelModeId],
        })
        .then(() => {
          message.success(translation.employee.travel_modes.deletePopConfirm.successMessage);
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  const saveName = useCallback((name: string) => {
    usePatch.mutateAsync({ travelModeId, patch: { name } });
  }, []);

  const debouncedSaveName = useMemo(() => _.debounce(saveName, 250), [saveName]);

  const updateName = (name: string) => {
    smartQueryClient.setQueryData<TravelMode>(
      ["employees", "travel_mode", travelModeId],
      (travelMode) => {
        return travelMode
          ? {
              ...travelMode,
              name,
            }
          : undefined;
      }
    );

    debouncedSaveName(name);
  };

  const formOnChange = () => {
    form
      .validateFields()
      .then(() => {
        updateName(form.getFieldValue("name"));
      })
      .catch(() => {
        console.log("name is required.");
      });
  };

  return (
    <>
      <PageHeader
        loading={dataIsLoading}
        title={data?.name}
        right={
          <Space>
            {usePatch.isLoading || usePost.isLoading ? (
              <span>
                <LoadingOutlined /> Saving...
              </span>
            ) : (
              <span>
                <CheckOutlined /> Saved
              </span>
            )}
            {dataIsLoading ||
            dataIsFetching ||
            usePost.isLoading ||
            deleteTravalModeReference.isLoading ? (
              <Skeleton style={{ height: "26px", width: "92px", alignSelf: "end" }} />
            ) : data?.status === "incomplete" ? (
              <StatusTag status={"incomplete"} />
            ) : (
              <StatusTag status={"complete"} />
            )}
          </Space>
        }
      />
      <Row>
        <Col span={18}>
          <Row
            className={styles.leftContainer}
            gutter={[8, 8]}
          >
            <Col
              span={24}
              className={styles.title}
            >
              <span>Impact</span>
            </Col>
            <Col span={24}>
              <Row
                className={styles.impactContainer}
                style={{
                  border: data?.status === "incomplete" ? "1px solid #FAAD14" : "1px solid #F0F0F0",
                }}
                align={"middle"}
              >
                <Col span={16}>
                  {dataIsLoading || OptionsIsLoading ? (
                    <Skeleton style={{ height: "30px", width: "100%" }} />
                  ) : (
                    <TravelModeCascader
                      urn={data?.urn}
                      onChange={onChange}
                      loading={
                        dataIsFetching || deleteTravalModeReference.isLoading || usePost.isLoading
                      }
                    />
                  )}
                </Col>
                <Col
                  span={8}
                  className={styles.impactStatus}
                >
                  {dataIsLoading ||
                  dataIsFetching ||
                  usePost.isLoading ||
                  deleteTravalModeReference.isLoading ? (
                    <Skeleton style={{ height: "26px", width: "92px", alignSelf: "end" }} />
                  ) : data?.status === "incomplete" ? (
                    <StatusTag status={"incomplete"} />
                  ) : (
                    <StatusTag status={"complete"} />
                  )}
                </Col>
                <Col
                  span={24}
                  className={styles.impactRow}
                >
                  <ImpactPill
                    impact={selectedOption?.impact}
                    type="borderless"
                    loading={dataIsLoading || dataIsFetching}
                    before={
                      selectedOption && (
                        <span className={styles.impactUnit}>1 {selectedOption.unit}</span>
                      )
                    }
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row
            className={styles.relationsContainer}
            gutter={[8, 8]}
          >
            <Col span={24}>
              <span className={styles.title}>Relations</span>
            </Col>
            <Col span={24}>
              <TravelRelations travelModeId={travelModeId} />
            </Col>
          </Row>
        </Col>
        <Col
          span={6}
          className={styles.rightContainer}
        >
          <Row>
            <Form
              layout="vertical"
              form={form}
              onChange={formOnChange}
              className={styles.inputContainerForm}
            >
              <Col span={24}>
                <span className={styles.title}>Details</span>
              </Col>
              <Col span={24}>
                <Space
                  direction={"vertical"}
                  size={1}
                  className={styles.inputContainer}
                >
                  <Form.Item
                    label={"Name"}
                    name={"name"}
                    required
                    rules={[
                      {
                        required: true,
                        message: "Name is required.",
                      },
                    ]}
                  >
                    <RootInput showSkeleton={dataIsLoading} />
                  </Form.Item>
                  <Form.Item
                    label={"ID"}
                    name={"id"}
                    required
                  >
                    <RootInput
                      disabled
                      showSkeleton={dataIsLoading}
                    />
                  </Form.Item>
                </Space>
              </Col>
              <Col span={24}>
                <DeleteTravelModeAction
                  travelModes={data && [data]}
                  onSuccess={onDelete}
                >
                  <Button
                    icon={<DeleteOutlined />}
                    disabled={data?.relations !== undefined ? data.relations > 0 : !data}
                    className={styles.deleteButton}
                    danger
                  >
                    Delete
                  </Button>
                </DeleteTravelModeAction>
              </Col>
            </Form>
          </Row>
        </Col>
      </Row>
    </>
  );
}

export default TravelModeDetail;
